import { TrackTypeEnum } from '@/entities/tracks'
import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type SorterResult } from 'antd/es/table/interface'
import { combine, createEvent, createStore, sample } from 'effector'
import { debounce } from 'patronum'

import {
  type GetStatisticsTracksQueryFinishedSuccessResult,
  type GetStatisticsTracksQueryFinishedSuccessResultTracksItem,
  type GetStatisticsTracksQueryStart,
  getStatisticsTracksQuery,
} from '../api'
import {
  parseTracksStatisticsSortByToSorterResult,
  parseTracksStatisticsSorterResultToSortBy,
} from '../lib/types/tracks'
import {
  $rangeAsParams,
  StatisticsPageGate,
  rangeDebounced,
} from './statistics-range.model'

// Track Type

export const trackTypeChanged =
  createEvent<GetStatisticsTracksQueryStart['params']['TrackType']>()
export const $trackType = createStore<
  GetStatisticsTracksQueryStart['params']['TrackType']
>(TrackTypeEnum.Music).on(trackTypeChanged, (_, payload) => payload)

// Sort By
export const sortByReset = createEvent()
export const sortByChanged =
  createEvent<GetStatisticsTracksQueryStart['params']['SortBy']>()
export const sortByChangedBySorterResult =
  createEvent<
    SorterResult<GetStatisticsTracksQueryFinishedSuccessResultTracksItem>
  >()
export const $sortBy = createStore<
  GetStatisticsTracksQueryStart['params']['SortBy']
>(undefined, {
  skipVoid: false,
})
  .on(sortByChanged, (_, payload) => payload)
  .on(sortByChangedBySorterResult, (_, payload) =>
    parseTracksStatisticsSorterResultToSortBy(payload),
  )
  .reset([sortByReset, StatisticsPageGate.close])

export const $sorterResult = $sortBy.map((sortBy) =>
  parseTracksStatisticsSortByToSorterResult(sortBy),
)

// Search Query
export const searchQueryChanged =
  createEvent<GetStatisticsTracksQueryStart['params']['SearchQuery']>()
export const $searchQuery = createStore<
  GetStatisticsTracksQueryStart['params']['SearchQuery']
>(undefined, {
  skipVoid: false,
}).on(searchQueryChanged, (_, payload) => payload)

// Pagination
export const pageChanged = createEvent<number>()
export const limitChanged = createEvent<number>()
const pageReset = createEvent()
export const $page = createStore<number>(DEFAULT_PAGE)
  .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
  .reset([pageReset, limitChanged, getStatisticsTracksQuery.reset])
export const $limit = createStore<number>(DEFAULT_PAGE_SIZE)
$limit.on(limitChanged, (_, size) => size)
const $offset = combine(
  { page: $page, limit: $limit },
  ({ page, limit }) => (page - 1) * limit,
)

sample({
  source: getStatisticsTracksQuery.$status,
  clock: [$trackType, $searchQuery, $sortBy],
  filter: (status) => status !== 'initial',
  target: pageReset,
})

// Request
const $request = combine(
  {
    sortBy: $sortBy,
    searchQuery: $searchQuery,
    trackType: $trackType,
    offset: $offset,
    limit: $limit,
  },
  ({
    sortBy,
    searchQuery,
    trackType,
    offset,
    limit,
  }): GetStatisticsTracksQueryStart => ({
    params: {
      TrackType: trackType,
      Limit: limit,
      Offset: offset,
      SortBy: sortBy,
      SearchQuery: searchQuery,
    },
  }),
)

const requestDebounced = debounce({
  source: $request,
  timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
})

export const $isLoadingInitially = combine(
  {
    pending: getStatisticsTracksQuery.$pending,
    data: getStatisticsTracksQuery.$data,
    error: getStatisticsTracksQuery.$error,
    status: getStatisticsTracksQuery.$status,
  },
  ({ pending, data, error, status }) =>
    (pending || status !== 'done') && !data && !error,
)

export const $isLoadingMore = combine(
  {
    pending: getStatisticsTracksQuery.$pending,
    data: getStatisticsTracksQuery.$data,
    error: getStatisticsTracksQuery.$error,
    status: getStatisticsTracksQuery.$status,
  },
  ({ pending, data, error }) => pending || (!data && !error),
)

export const $tableTotalItems = createStore<number>(0)
  .on(getStatisticsTracksQuery.finished.success, (_, payload) => {
    const { tracks } = payload.result
    return tracks?.totalItems || 0
  })
  .reset(getStatisticsTracksQuery.reset)

export const $tableData = createStore<
  NonNullable<GetStatisticsTracksQueryFinishedSuccessResult['tracks']>['items']
>([])
  .on(getStatisticsTracksQuery.finished.success, (_, payload) => {
    const tracks = payload.result.tracks
    return tracks ? tracks.items : []
  })
  .reset(getStatisticsTracksQuery.reset)

export const $totalData = createStore<Omit<
  GetStatisticsTracksQueryFinishedSuccessResult,
  'tracks'
> | null>(null)
  .on(getStatisticsTracksQuery.finished.success, (_, payload) => {
    const { tracks, ...rest } = payload.result
    return rest
  })
  .reset(getStatisticsTracksQuery.reset)

sample({
  source: combine({ request: $request, range: $rangeAsParams }),
  clock: [rangeDebounced, requestDebounced],
  fn: ({ request, range }): GetStatisticsTracksQueryStart => ({
    params: {
      ...request.params,
      ...range.params,
    },
  }),
  target: getStatisticsTracksQuery.start,
})

sample({
  clock: StatisticsPageGate.close,
  target: getStatisticsTracksQuery.reset,
})
